import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";

const initialState = {
  compareStartDate: null,
  compareEndDate: null,
  countryFilter: "All",
  gaDeviceCategoriesFilter: "All",
  gaSourceFilter: "All",
  gaLandingPageFilter: "All",
  selectedConfig: null,
  rollingWindow: 7,
  IsRolling: false,
  bulkUploaderAccessToken: null,
  bulkUploaderFacebookAccessToken: null,
  bulkUploaderSelectedClientId: null,
  bulkUploaderSelectedJobName: null,
  whitelistedMediaGranularityLevel: "username",
  selectedCampaigns: [],
  platformCampaigns: {},
  selectedChannels: [],
  platformChannels: {},
  selectedProductGroup: null,
  isAutoCompareDates: true,
  selectedInfluencerUsernameContains: "",
  ltr_vs_ltv: "ltr",
  ltr_filter: "All",
  selectedDropdownValuesPerDropdownGroup: {
    "default": {
      "client_name": ""
    },
    "site_analytics": {
      "client_name": ""
    },
    "settings": {
      "client_name": ""
    },
    "influencer_analytics": {
      "client_name": ""
    },
    "influencer_content": {
      "client_name": ""
    }
  },
  defaultsFetchedForDropdown: {
    "default": {
    },
    "site_analytics": {
    },
    "settings": {
    },
    "influencer_analytics": {
    },
    "influencer_content": {
    }
  },
  selectedDropdownValues: {'client_name': ""},
  currentDropdownGroup: 'default',
  dateRangeManuallyUpdated: false
};

// Helper functions for shared dropdown logic
const setValueAcrossAllGroups = (state, objectKey, key, value) => {
  Object.keys(state[objectKey]).forEach(group => {
    state[objectKey][group][key] = value;
  });
};

const setDropdownValue = (state, dropdownName, value, isShared = false) => {
  if (isShared) {
    setValueAcrossAllGroups(state, 'selectedDropdownValuesPerDropdownGroup', dropdownName, value);
  } else {
    // Ensure the current dropdown group exists
    if (!state.selectedDropdownValuesPerDropdownGroup[state.currentDropdownGroup]) {
      state.selectedDropdownValuesPerDropdownGroup[state.currentDropdownGroup] = {};
    }
    state.selectedDropdownValuesPerDropdownGroup[state.currentDropdownGroup][dropdownName] = value;
  }
  state.selectedDropdownValues[dropdownName] = value;
};

const setDefaultsFetched = (state, dropdownName, value, isShared = false) => {
  if (isShared) {
    setValueAcrossAllGroups(state, 'defaultsFetchedForDropdown', dropdownName, value);
  } else {
    // Ensure the current dropdown group exists
    if (!state.defaultsFetchedForDropdown[state.currentDropdownGroup]) {
      state.defaultsFetchedForDropdown[state.currentDropdownGroup] = {};
    }
    state.defaultsFetchedForDropdown[state.currentDropdownGroup][dropdownName] = value;
  }
};

const genericReportingSlice = createSlice({
  name: "genericReporting",
  initialState,
  reducers: {
    setSelectedClient: (state, value) => {
      setDropdownValue(state, 'client_name', value.payload, true);
    },
    setStartAndEndDate: (state, value) => {
      setDropdownValue(state, 'start_date', value.payload[0]);
      setDropdownValue(state, 'end_date', value.payload[1]);
    },
    setCompareStartAndEndDate: (state, value) => {
      setDropdownValue(state, 'compare_start_date', value.payload[0]);
      setDropdownValue(state, 'compare_end_date', value.payload[1]);
    },
    setSelectedDropdownValuesPerDropdownGroup: (state, action) => {
      state.selectedDropdownValuesPerDropdownGroup = action.payload;
    },
    setSelectedDropdownValues: (state, action) => {
      const { dropdownName, value } = action.payload;
      const isShared = dropdownName === 'client_name' || dropdownName === 'client_region' || dropdownName === 'single_select_client_region';
      setDropdownValue(state, dropdownName, value, isShared);
    },
    setDefaultsFetchedForDropdown: (state, action) => {
      const { dropdownName, value } = action.payload;
      const isShared = dropdownName === 'client_name' || dropdownName === 'client_region' || dropdownName === 'single_select_client_region';
      setDefaultsFetched(state, dropdownName, value, isShared);
    },
    setCurrentDropdownGroup: (state, value) => {
      state.currentDropdownGroup = value.payload;
    },
    applyCurrentDropdownGroupToSelectedDropdownValues: (state) => {
      // Ensure the current dropdown group exists
      if (!state.selectedDropdownValuesPerDropdownGroup[state.currentDropdownGroup]) {
        state.selectedDropdownValuesPerDropdownGroup[state.currentDropdownGroup] = {};
      }
      state.selectedDropdownValues = state.selectedDropdownValuesPerDropdownGroup[state.currentDropdownGroup];
      // Ensure client_name is always available since it's shared
      const clientName = state.selectedDropdownValuesPerDropdownGroup[state.currentDropdownGroup]['client_name'];
      if (clientName) {
        state.selectedDropdownValues['client_name'] = clientName;
      }
    },
    setCountryFilter: (state, value) => {
      state.countryFilter = value.payload;
    },
    setGaDeviceCategoriesFilter: (state, value) => {
      state.gaDeviceCategoriesFilter = value.payload;
    },
    setGaSourceFilter: (state, value) => {
      state.gaSourceFilter = value.payload;
    },
    setGaLandingPageFilter: (state, value) => {
      state.gaLandingPageFilter = value.payload;
    },
    setSelectedConfig: (state, value) => {
      state.selectedConfig = value.payload;
    },
    setRollingWindow: (state, value) => {
      state.rollingWindow = value.payload;
    },
    setIsRolling: (state, value) => {
      state.IsRolling = value.payload;
    },
    setBulkUploaderAccessToken: (state, value) => {
      state.bulkUploaderAccessToken = value.payload;
    },
    removeBulkUploaderAccessToken: (state) => {
      state.bulkUploaderAccessToken = null;
    },
    setBulkUploaderFacebookAccessToken: (state, value) => {
      state.bulkUploaderFacebookAccessToken = value.payload;
    },
    removeBulkUploaderFacebookAccessToken: (state) => {
      state.bulkUploaderFacebookAccessToken = null;
    },
    setBulkUploaderSelectedClientId: (state, value) => {
      state.bulkUploaderSelectedClientId = value.payload;
    },
    setBulkUploaderSelectedJobName: (state, value) => {
      state.bulkUploaderSelectedJobName = value.payload;
    },
    setWhitelistedMediaGranularityLevel: (state, value) => {
      state.whitelistedMediaGranularityLevel = value.payload;
    },
    setPlatformCampaigns: (state, value) => {
      state.platformCampaigns = value.payload;
    },
    setSelectedChannels: (state, value) => {
      state.selectedChannels = value.payload;
    },
    setPlatformChannels: (state, value) => {
      state.platformChannels = value.payload;
    },
    setSelectedProductGroup: (state, value) => {
      state.selectedProductGroup = value.payload;
    },
    setIsAutoCompareDates: (state, value) => {
      state.isAutoCompareDates = value.payload;
    },
    setSelectedInfluencerUsernameContains: (state, value) => {
      state.selectedInfluencerUsernameContains = value.payload;
    },
    setLtrVsLtv: (state, value) => {
      state.ltr_vs_ltv = value.payload;
    },
    setLtrFilter: (state, value) => {
      state.ltr_filter = value.payload;
    },
    setDateRangeManuallyUpdated: (state, value) => {
      state.dateRangeManuallyUpdated = value.payload;
    },
  },
});

export const {
  setBulkUploaderSelectedClientId,
  setBulkUploaderSelectedJobName,
  setBulkUploaderFacebookAccessToken,
  removeBulkUploaderFacebookAccessToken,
  setBulkUploaderAccessToken,
  removeBulkUploaderAccessToken,
  setSelectedClient,
  setStartAndEndDate,
  setCountryFilter,
  setGaDeviceCategoriesFilter,
  setGaSourceFilter,
  setSelectedConfig,
  setGaLandingPageFilter,
  setRollingWindow,
  setIsRolling,
  setWhitelistedMediaGranularityLevel,
  setCompareStartAndEndDate,
  setPlatformCampaigns,
  setSelectedChannels,
  setPlatformChannels,
  setSelectedProductGroup,
  setIsAutoCompareDates,
  setSelectedInfluencerUsernameContains,
  setLtrVsLtv,
  setLtrFilter,
  setSelectedDropdownValues,
  setCurrentDropdownGroup,
  applyCurrentDropdownGroupToSelectedDropdownValues,
  setSelectedDropdownValuesPerDropdownGroup,
  setDefaultsFetchedForDropdown,
  setDateRangeManuallyUpdated,
} = genericReportingSlice.actions;

export default genericReportingSlice.reducer;
